// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import { useSelector } from 'react-redux';
import { useSafetySuspended } from '../../util/safetySuspended';
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';
import { listingBookingLinkProps } from '../../util/bookingIntent';
import { listingPageFromTo } from '../../util/listingBack';
import { talkerBookingDisplayName } from '../../util/listingCardCopy';
import {
  LISTENER_PRIMARY_CTA,
  listenerPrimaryCtaMessageId,
  resolveListenerAvailability,
} from '../../util/listenerAvailability';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';
import VerifiedBadge from '../VerifiedBadge/VerifiedBadge';
import { ListingFreeBadge } from '../FirstSessionFree/FirstSessionFree';
import FoundingListenerBadge from '../FoundingListenerBadge/FoundingListenerBadge';
import AvailableNowBadge, { AvailableNowDot } from '../AvailableNowBadge/AvailableNowBadge';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import ListingRating from '../ListingRating/ListingRating';
import useReviewStats from '../../util/useReviewStats';
import { emptyReviewStats } from '../../util/listingReviews';

import { getListingCardTranslations } from './ListingCard.helpers';

import { HeartAvatarFace } from '../HeartAvatar/HeartAvatar';

import { CardLovesToChat } from '../TalkAbout/TalkAbout';
import ThumbCtas from '../ThumbCtas/ThumbCtas';
import HelloPlayBadge from '../HelloVideo/HelloPlayBadge';
import css from './ListingCard.module.css';

const SUSPENDED_CTA_STYLE = {
  opacity: 0.5,
  cursor: 'not-allowed',
  pointerEvents: 'none',
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, {
  loadAfterInitialRendering: 3000,
});

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
    status,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      {firstImage && variants.length ? (
        <ImageComponent
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      ) : (
        <HeartAvatarFace user={listing?.author} listener square className={css.heartCover} />
      )}
      <AvailableNowDot status={status} />
    </AspectRatioWrapper>
  );
};

const bookingModeForCta = primaryCta => {
  if (primaryCta === LISTENER_PRIMARY_CTA.TALK_NOW) {
    return 'now';
  }
  if (primaryCta === LISTENER_PRIMARY_CTA.BOOK_NEXT) {
    return 'next';
  }
  return 'later';
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    hideViewProfile = false,
    lazyLoadImage = true,
    online = false,
    sessionBusy = false,
    startNowPaused = false,
    nextAvailableAt = null,
    reviewStats,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    oneLinerFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);
  const hasCurrentUser = useSelector(state => !!state?.user?.currentUser?.id);
  const accountSuspended = useSafetySuspended(hasCurrentUser);

  const id = listing?.id?.uuid;
  const fetchedStatsMap = useReviewStats(reviewStats || !id ? [] : [id]);
  const stats = reviewStats || fetchedStatsMap[id] || emptyReviewStats();
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);
  const availability = resolveListenerAvailability({
    listing,
    online,
    sessionBusy,
    startNowPaused,
    nextAvailableAt,
  });

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const primaryLink = listingBookingLinkProps({
    id,
    slug,
    mode: bookingModeForCta(availability.primaryCta),
    nextAvailableAt: availability.nextAvailableAt,
  });
  const laterLink = listingBookingLinkProps({ id, slug, mode: 'later' });
  const chooseName = talkerBookingDisplayName(listing, listing?.author);
  const primaryClass =
    availability.primaryCta === LISTENER_PRIMARY_CTA.TALK_NOW
      ? classNames(css.book, css.talkNow)
      : css.book;

  return (
    <article className={classes} data-listing-card="true" data-availability={availability.status}>
      <NamedLink
        className={css.cardLink}
        name="ListingPage"
        params={{ id, slug }}
        to={listingPageFromTo('browse')}
        ariaLabel={cardAriaLabel}
      >
        {showListingImage ? (
          <ListingCardImage
            renderSizes={renderSizes}
            title={titlePlain}
            listing={listing}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
            aspectRatioClassName={aspectRatioClassName}
            lazyLoadImage={lazyLoadImage}
            status={availability.status}
          />
        ) : (
          <div className={css.photoWrap}>
            <ListingCardThumbnail
              style={cardStyle}
              listingTitle={title}
              className={aspectRatioClassName}
              width={aspectWidth}
              height={aspectHeight}
              setActivePropsMaybe={setActivePropsMaybe}
            />
            <AvailableNowDot status={availability.status} />
          </div>
        )}
        <div className={css.info}>
          <div className={css.mainInfo}>
            {showAuthorInfo ? (
              <div
                className={classNames(css.authorInfo, {
                  [css.lightText]: darkMode,
                })}
              >
                <span className={css.authorNameText}>{authorName}</span>
                {/* Sam Nov 1 (t5748u): bookmark off the photo, beside the name */}
                <FavoriteButton listingId={id} listing={listing} className={css.favoriteOnCard} />
              </div>
            ) : (
              <FavoriteButton listingId={id} listing={listing} className={css.favoriteOnCard} />
            )}
            {oneLinerFormatted ? (
              <div
                className={classNames(css.oneLiner, {
                  [css.lightText]: darkMode,
                })}
              >
                {oneLinerFormatted}
              </div>
            ) : null}
            <CardLovesToChat listing={listing} />
            <div className={css.badgeRow}>
              <FoundingListenerBadge listing={listing} user={listing?.author} />
              <VerifiedBadge user={listing?.author} />
              <AvailableNowBadge
                status={availability.status}
                nextAvailableAt={availability.nextAvailableAt}
                timeZone={listing?.attributes?.availabilityPlan?.timezone}
              />
            </div>
            <HelloPlayBadge userId={listing?.author?.id?.uuid} name={title} size="chip" className={css.cardHelloBadge} />
            <ListingRating
              className={classNames(css.rating, { [css.lightText]: darkMode })}
              average={stats.average}
              count={stats.count}
            />
            {showPrice ? (
              <div className={css.price} title={priceTooltip} style={{ fontSize: 13, opacity: 0.75 }}>
                {priceMessage}
              </div>
            ) : null}
          </div>
        </div>
      </NamedLink>
      <ThumbCtas listing={listing} availability={availability} disabled={accountSuspended} />
      {hideViewProfile ? null : (
        <NamedLink
          className={css.viewProfile}
          name="ListingPage"
          params={{ id, slug }}
          to={listingPageFromTo('browse')}
          data-view-profile="true"
        >
          <FormattedMessage id="ListingCard.viewProfile" />
        </NamedLink>
      )}
    </article>
  );
};

export default ListingCard;
